import React, { useState, useEffect } from "react";
import { CircularProgress, Box } from "@mui/material";
import Swal from "sweetalert2";
import {
  employeeFormFields,
  employeeFormSteps,
} from "./EmployeeFormSteps";
import {
  generateAndExecuteMutation,
  graphqlQuery,
  fetchDataEngine,
} from "../../../data/Axios/DynamicService";
import {
  accountsmanagement,
  companyManagementUrl,
  HRService,
  setupManagementUrl,
  userManagementUrl,
  AccountsandFinanceurl
} from "../../../config";
import DynamicForm from "../../../data/Axios/DynamicForm";
import * as yup from "yup";


const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  // Handles ISO strings (2023-11-25T00:00:00)
  // And handles standard JS Date strings
  try {
    return new Date(dateString).toISOString().split("T")[0];
  } catch (e) {
    return "";
  }
};

const base_url = HRService.uri;

// Phone validation regex pattern for consistency
const PHONE_REGEX = /^(0[17]\d{8}|\+254[17]\d{8})$/;
const PHONE_ERROR_MESSAGE = "Enter a valid phone number (0712345678, 0112345678 or +254712345678)";

// Employee Validation Schema
export const employeeValidationSchema = yup.object().shape({

  /* -------------------------------------------------------------
     STEP 1 – PERSONAL INFORMATION
  ------------------------------------------------------------- */
  firstName: yup.string().required("First Name is required"),
  surname: yup.string().required("Surname is required"),
  lastName: yup.string().nullable(),

  idNumber: yup
    .string()
    .matches(
      /^\d{8}$|^\d{11}$/,
      "Enter a valid ID No.)"
    )
    .required("ID Number is required"),

  genderId: yup
    .string()
    .required("Gender is required"),

  dob: yup
    .date()
    .required("Date of Birth is required")
    .test(
      "is-adult",
      "Employee must be at least 18 years old",
      function (value) {
        // Let the required() rule handle missing values
        if (!value) return true;

        const today = new Date();
        const birthDate = new Date(value);

        //Calculate age
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        // Adjust age if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        return age >= 18;
      }
    ),
  /* -------------------------------------------------------------
     STEP 2 – CONTACT INFORMATION
  ------------------------------------------------------------- */
  mobileNumber: yup
    .string()
    .matches(
      PHONE_REGEX,
      PHONE_ERROR_MESSAGE
    )
    .required("Mobile Number is required"),

  emailAddress: yup
    .string()
    .email("Enter a valid email address")
    .required("Email Address is required"),

  regionId: yup.string().nullable(),
  branchId: yup.string().nullable(),

  /* -------------------------------------------------------------
     STEP 3 – EMPLOYMENT DETAILS
  ------------------------------------------------------------- */
  deptId: yup.string().required("Department is required"),
  jobTitle: yup.string().required("Job Title is required"),

  //salaryBandId: yup.string().nullable(),

  hireDate: yup.date().required("Hire Date is required"),
  terminationDate: yup.date().nullable(),

  /* -------------------------------------------------------------
     STEP 4 – COMPENSATION
  ------------------------------------------------------------- */
  // baseSalary: yup
  //   .number()
  //   .typeError("Base Salary must be a number")
  //   .nullable()
  //   .min(0, "Base Salary cannot be negative"),

  /* -------------------------------------------------------------
     STEP 5 – STATUTORY INFORMATION
  ------------------------------------------------------------- */
  kraPin: yup
    .string()
    .matches(
      /^[A-Z]\d{9}[A-Z]$/,
      "Enter a valid KRA PIN (A123456789B)"
    )
    .required("KRA PIN is required"),

  nssfNo: yup.string().nullable(),
  shifNo: yup.string().nullable(),
  helbNo: yup.string().nullable(),

  /* -------------------------------------------------------------
     STEP 6 – BANK DETAILS
  ------------------------------------------------------------- */
  bankId: yup.string().nullable(),
  bankBranchId: yup.string().nullable(),
  accountNumber: yup.string().nullable(),

  /* -------------------------------------------------------------
     STEP 7 – NEXT OF KIN
  ------------------------------------------------------------- */
  nokFirstName: yup.string().nullable(),
  nokLastName: yup.string().nullable(),

  nokRelationShip: yup.string().nullable(),

  nokPhone: yup
    .string()
    .nullable()
    .when(["nokFirstName", "nokLastName"], {
      is: (f, l) => (f && f.trim() !== "") || (l && l.trim() !== ""),
      then: (schema) =>
        schema
          .matches(
            PHONE_REGEX,
            PHONE_ERROR_MESSAGE
          )
          .notOneOf([yup.ref("mobileNumber")], "NOK Phone Number cannot be the similar to Employee's contact Number")
          .required("Next of Kin phone is required when NOK name is provided"),
      otherwise: (schema) => schema.nullable(),
    }),

  /* -------------------------------------------------------------
     STEP 8 – SYSTEM USER LINK (OPTIONAL)
  ------------------------------------------------------------- */
  userId: yup.string().nullable(),
});


const EmployeeCreationForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState({
    regions: [],
    branches: [],
    practiceGroups: [],
    //salaryBands: [],
    genders: [],
    relations: [],
    banks: [],
    bankBranches: [],
    users: [],
    allBankBranches: [], 
  });

  /* ------------------------------------------------------------------ */
  /*  INITIAL VALUES – NOW INCLUDES userId                             */
  /* ------------------------------------------------------------------ */
  const initialValues = {
    // Step 1 – Personal
    firstName: props.data?.firstName || "",
    surname: props.data?.surname || "",
    lastName: props.data?.lastName || "",
    idNumber: props.data?.idNo || "",
    genderId: props.data?.genderId || "",
    dob: formatDateForInput(props.data?.dob),

    // Step 2 – Contact
    mobileNumber: props.data?.mobileNumber || "",
    emailAddress: props.data?.emailAddress || "",
    regionId: props.data?.regionId || "",
    branchId: props.data?.branchId || "",

    // Step 3 – Employment
    deptId: props.data?.deptId || "",
    jobTitle: props.data?.jobTitle || "",
   // salaryBandId: props.data?.salaryBandId || "",
    hireDate: formatDateForInput(props.data?.dateOfEmployment || props.data?.hireDate),
    terminationDate: formatDateForInput(props.data?.terminationDate),

    // Step 4 – Compensation
   // baseSalary: props.data?.baseSalary || "",
    processPayroll: props.data?.processPayroll ?? true,

    // Step 5 – Statutory
    kraPin: props.data?.kraPin || "",
    nssfNo: props.data?.nssfNo || "",
    shifNo: props.data?.shifNo || "",
    helbNo: props.data?.helbNo || "",

    // Step 6 – Bank
    bankId: props.data?.bankId ?? "",
    bankBranchId: props.data?.bankBranchId ?? "",
    accountNumber: props.data?.accountNumber || "",

    // Step 7 – Next of Kin
    nokFirstName: props.data?.nokFirstName || "",
    nokLastName: props.data?.nokLastName || "",
    nokRelationShip: props.data?.nokRelationShip || "",
    nokPhone: props.data?.nokPhone || "",

    // Step 8 – System User Link
    userId: props.data?.userId || "", // <-- NEW: optional link to system user
  };

  /* ------------------------------------------------------------------ */
  /*  FETCH SETUP DATA – NOW INCLUDES USERS                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    fetchSetupData();
  }, []);

  const fetchSetupData = async () => {
    try {
      setLoading(true);

      const queries = {
        regions: `query { allRegions { id regionName } }`,
        practiceGroups: `query { allPracticeGroups { id name } }`,
        //salaryBands: `query { allSalaryBands { id bandName } }`,
        genders: `query { allGenders { id gender } }`,
        relations: `query { allRelationshipTypes { id relationshipName } }`,
        banks: `query { allBanks { id bankName } }`,
        users: `query { allUsers { id username email } }`, // <-- fetch system users
        allBankBranches: `query { allBankBranchesViewByColumnValues(bankBranchesViewModel: {}) { id branchCode branchName branchSwiftCode bankName bankId useInPrintout } }`,
      };

      const [
        regionsData,
        practiceGroupsData,
        //salaryBandsData,
        gendersData,
        relationsData,
        banksData,
        usersData,
        allBankBranchesData,
      ] = await Promise.all([
        graphqlQuery(queries.regions, companyManagementUrl.uri).catch((err) => {
          console.error("REGION QUERY FAILED:", err);
          return [];
        }),
        graphqlQuery(queries.practiceGroups, base_url).catch(() => []),
        //graphqlQuery(queries.salaryBands, base_url).catch(() => []),
        graphqlQuery(queries.genders, setupManagementUrl.uri).catch(() => []),
        graphqlQuery(queries.relations, base_url).catch(() => []),
        graphqlQuery(queries.banks, accountsmanagement.uri).catch(() => []),
        graphqlQuery(queries.users, userManagementUrl.uri).catch(() => []),
        graphqlQuery(queries.allBankBranches, AccountsandFinanceurl.uri).catch(() => ({})),
      ]);

      setSetupData({
        regions: regionsData || [],
        practiceGroups: practiceGroupsData || [],
        //salaryBands: salaryBandsData || [],
        genders: gendersData || [],
        relations: relationsData || [],
        banks: banksData || [],
        users: usersData || [],
        allBankBranches: allBankBranchesData || [],
        branches: [],
        bankBranches: [],
      });
    } catch (error) {
      console.error("Error fetching setup data:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load form data. Please refresh.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.isEditing && setupData.allBankBranches.length > 0) {
      const initialBankId = initialValues.bankId;

      if (initialBankId) {
        
        const branchesForInitialBank = setupData.allBankBranches
          .filter(b => b.bankId === parseInt(initialBankId))
          .map(b => ({
            value: b.id,
            label: b.branchName,
          }));

        if (branchesForInitialBank.length > 0) {
          setSetupData(prev => ({ 
            ...prev, 
            bankBranches: branchesForInitialBank 
          }));
        }
      }
    }
  }, [props.isEditing, initialValues.bankId, setupData.allBankBranches.length]);


  
  useEffect(() => {
    const initialRegionId = initialValues.regionId;

    // Only run if in edit mode, a region is set, and the branches haven't been loaded yet.
    if (props.isEditing && initialRegionId && setupData.branches.length === 0) {
        
      const loadInitialCompanyBranches = async (regionId) => {
        try {
          const regionIdInt = parseInt(regionId);
          const input = { regionId: regionIdInt };
          const returnFields = ["id", "branchName"];
          const url = companyManagementUrl.uri;

          let rawData = await fetchDataEngine(
            "allBranchesByColumnValues",
            "branchModel",
            input,
            returnFields,
            url
          );

          const branchArray = Array.isArray(rawData) ? rawData : (rawData?.allBranchesByColumnValues || rawData || []);
          
          const opts = branchArray.map((b) => ({ 
            value: b.id, 
            label: b.branchName || `Branch ID: ${b.id}` 
          }));

          if (opts.length > 0) {
            setSetupData((prev) => ({ ...prev, branches: opts }));
          }
        } catch (e) {
          console.error("CRITICAL: Initial Company branch fetch failed in edit mode.", e);
        }
      };

      loadInitialCompanyBranches(initialRegionId);
    }
  }, [props.isEditing, initialValues.regionId, setupData.branches.length]);

  /* ------------------------------------------------------------------ */
  /*  POPULATE SELECT OPTIONS – NOW INCLUDES userId                    */
  /* ------------------------------------------------------------------ */
  const populatedFields = employeeFormFields.map((field) => {
    let options = field.options || [];

    switch (field.name) {
      case "regionId":
        options = (setupData.regions && setupData.regions.length > 0)
          ? setupData.regions.map((r) => ({ value: r.id, label: r.regionName }))
          : [];
        console.log(`[FORM] Region field options:`, options, "setupData.regions:", setupData.regions);
        break;
      case "branchId":
        options = setupData.branches;
        break;
      case "deptId":
        options = setupData.practiceGroups.map((p) => ({ value: p.id, label: p.name }));
        break;
      // case "salaryBandId":
      //   options = setupData.salaryBands.map((s) => ({ value: s.id, label: s.bandName }));
      //   break;
      case "genderId":
        options = setupData.genders.map((g) => ({ value: g.id, label: g.gender }));
        break;
      case "nokRelationShip":
        options = setupData.relations.map((r) => ({ value: r.id, label: r.relationshipName }));
        break;
      case "bankId":
        options = setupData.banks.map((b) => ({ value: b.id, label: b.bankName }));
        break;
      case "bankBranchId":
        options = setupData.bankBranches;
        break;
      case "userId": // <-- NEW: populate system users
        options = setupData.users.map((u) => ({
          value: u.id,
          label: `${u.username} (${u.email})`,
        }));
        break;
      default:
        break;
    }

    return { ...field, options };
  });

  /* ------------------------------------------------------------------ */
  /*  DEPENDENT FIELD LOADING                                          */
  /* ------------------------------------------------------------------ */
  const handleFieldChange = async (field, value, setFieldValue) => {
  console.log(`Field changed: ${field.name}, Value: ${value}`); 

   if (field.name === "regionId" && value) {
        try {
            const regionIdInt = parseInt(value);
            const input = { regionId: regionIdInt };
            const returnFields = ["id", "branchName"];
            const url = companyManagementUrl.uri;

            

            let rawData = await fetchDataEngine(
                "allBranchesByColumnValues",
                "branchModel",
                input,
                returnFields,
                url
            );

            const branchArray = Array.isArray(rawData) ? rawData : (rawData?.allBranchesByColumnValues || rawData || []);
            
            const opts = branchArray.map((b) => ({ 
                value: b.id, 
                label: b.branchName || `Branch ID: ${b.id}` 
            }));

            setSetupData((prev) => ({ ...prev, branches: opts }));
            setFieldValue("branchId", "");
            
            return {
                branchId: opts
            };

        } catch (e) {
            console.error("CRITICAL: Company branch fetch failed.", e);
            setSetupData((prev) => ({ ...prev, branches: [] }));
            setFieldValue("branchId", "");
            return {};
        }
    }



  if (field.name === "bankId" && value) {
    try {
      const input = { bankId: parseInt(value) };
      const returnFields = ["id", "branchName"];

      let data = await fetchDataEngine(
        "allBankBranchesByColumnValues",
        "bankBranchModel",
        input,
        returnFields,
        AccountsandFinanceurl.uri
      ).catch(() => []);

    
      if (!data.length) {
        data = setupData.allBankBranches.filter(
          (b) => b.bankId === parseInt(value)
        );
      }

      const opts = data.map((b) => ({
        value: b.id,          
        label: b.branchName,  
      }));

      setSetupData((prev) => ({ ...prev, bankBranches: opts }));
      setFieldValue("bankBranchId", "");
      
      
      return {
          bankBranchId: opts 
      };

    } catch (e) {
      console.error("Bank branch fetch error:", e);
      setFieldValue("bankBranchId", "");
      
      return {}; 
    }
  }

   
   return {};
  };

  /* ------------------------------------------------------------------ */
  /*  SUBMIT – INCLUDE userId IN MUTATION                              */
  /* ------------------------------------------------------------------ */
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let age = null;
      if (values.dob) {
        const birth = new Date(values.dob);
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        if (
          today.getMonth() < birth.getMonth() ||
          (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
        ) {
          age--;
        }
      }

      const mutationData = {
        firstName: values.firstName,
        surname: values.surname,
        lastName: values.lastName || null,
        idNo: values.idNumber,
        genderId: values.genderId || null,
        dob: values.dob || null,

        mobileNumber: values.mobileNumber,
        emailAddress: values.emailAddress,
        regionId: values.regionId ? parseInt(values.regionId) : null,
        branchId: values.branchId ? parseInt(values.branchId) : null,

        deptId: values.deptId ? parseInt(values.deptId) : null,
        jobTitle: values.jobTitle,
        //salaryBandId: values.salaryBandId ? parseInt(values.salaryBandId) : null,
        hireDate: values.hireDate,
        terminationDate: values.terminationDate || null,

        //baseSalary: values.baseSalary ? parseFloat(values.baseSalary) : null,
        processPayroll: values.processPayroll,

        kraPin: values.kraPin,
        nssfNo: values.nssfNo || null,
        shifNo: values.shifNo || null,
        helbNo: values.helbNo || null,

        bankId: values.bankId ? parseInt(values.bankId) : null,
        bankBranchId: values.bankBranchId ? parseInt(values.bankBranchId) : null,
        accountNumber: values.accountNumber || null,

        nokFirstName: values.nokFirstName || null,
        nokLastName: values.nokLastName || null,
        nokRelationShip: values.nokRelationShip ? parseInt(values.nokRelationShip) : null,
        nokPhone: values.nokPhone || null,

        // LINK TO SYSTEM USER (optional)
        userId: values.userId ? parseInt(values.userId) : null,

        companyId,
        age,
        currency: "KES",
      };

      if (props.isEditing) {
        mutationData.id = props.data.id;
      }

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateEmployee" : "createEmployee",
        mutationData,
        "employee",
        base_url
      );

      if (Object.values(response)[0]) {
        if (props.onClose) props.onClose();
        if (props.onAction) props.onAction();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: props.isEditing
            ? "Employee updated successfully."
            : "Employee created successfully.",
        });
      }
    } catch (error) {
      console.error("Error submitting employee:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                           */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={populatedFields}
      steps={employeeFormSteps}
      validationSchema={employeeValidationSchema}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      title={props.isEditing ? "Update Employee" : "Create New Employee"}
      subtitle="Link to a system user if this employee has login access"
    />
  );
};

export default EmployeeCreationForm;